import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { atualizarSaldo } from '../../../store/modules/parceiro/actions';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Container } from 'react-bootstrap';
import api from '../../../services/API';
import sortFunc from '../../../utils/ordernarColunaInt';
import moment from 'moment-timezone';

const formatarDataPrevisao = (celula, valor) => {
  if (valor.viaData) {
    if (valor.viaData?.entrega?.erro)
      return 'Erro. Entre em contato com o administrador';
    if (!valor.viaData?.entrega?.entregas[0]?.dataPrevisao) return '-';
    return moment(
      valor.viaData.entrega.entregas[0].dataPrevisao,
      'DD/MM/YYYY HH:mm'
    ).format('DD/MM/YYYY');
  }

  return '-';
};

const formatarDataEntrega = (celula, valor) => {
  if (valor.viaData) {
    if (valor.viaData?.entrega?.erro)
      return 'Erro. Entre em contato com o administrador';
    if (!valor.viaData?.entrega?.entregas[0]?.dataEntrega) return '-';
    return moment(
      valor.viaData.entrega.entregas[0].dataEntrega,
      'DD/MM/YYYY HH:mm'
    ).format('DD/MM/YY');
  }

  return '-';
};

const formatarData = (celula, valor) => {
  return moment(valor.resgatadoEm).format('DD/MM/YYYY HH:mm:ss');
};

const formatarQtde = (celula, valor) => {
  return Number(valor.qtde).toLocaleString('pt-BR');
};

const formatarSaldo = (celula, valor) => {
  return Number(valor.saldoAnterior).toLocaleString('pt-BR');
};

const formatarGasto = (celula, valor) => {
  return Number(valor.pontosGastos).toLocaleString('pt-BR');
};

const formatarStatus = (celula, valor) => {
  if (valor.status === 0) {
    return (
      <span>
        <i className="fa fa-times"></i> Não
      </span>
    );
  } else if (valor.status === 1) {
    return (
      <span>
        <i className="fa fa-check"></i> Entregue
      </span>
    );
  } else if (valor.status === 2) {
    return (
      <span>
        <i className="fa fa-truck"></i> Em transporte
      </span>
    );
  } else if (valor.status === 3) {
    return (
      <span>
        <i className="fa fa-times"></i> Cancelado
      </span>
    );
  } else if (valor.status === 4) {
    return (
      <span>
        <i className="fa fa-times"></i> Aguardando liberação
      </span>
    );
  } else if (valor.status === 5) {
    return (
      <span>
        <i className="fa fa-times"></i> Cancelado pelo fornecedor
      </span>
    );
  } else if (valor.status === 6) {
    return (
      <span>
        <i className="fa fa-check"></i> Liberado
      </span>
    );
  }
};

const formatarPremioResgatado = (celula, valor) => {
  return valor.obs ? `${valor.premio} - ${valor.obs}` : `${valor.premio}`;
};

const columns = [
  {
    dataField: 'premio',
    text: 'Prêmio Resgatado',
    formatter: formatarPremioResgatado,
  },
  {
    dataField: 'qtde',
    text: 'Quantidade',
    formatter: formatarQtde,
    sortFunc: sortFunc,
    sort: true,
  },
  {
    dataField: 'pontosGastos',
    text: 'Pontos Gastos',
    formatter: formatarGasto,
    sortFunc: sortFunc,
    sort: true,
  },
  {
    dataField: 'saldoAnterior',
    text: 'Saldo Anterior',
    formatter: formatarSaldo,
    sortFunc: sortFunc,
    sort: true,
  },
  {
    dataField: 'status',
    text: 'Entregue',
    formatter: formatarStatus,
  },
  {
    dataField: 'resgatadoEm',
    text: 'Data do Resgate',
    formatter: formatarData,
    sort: true,
  },
  {
    dataField: 'valor.viaData.entrega.entregas[0].dataEntrega',
    text: 'Data de Entrega',
    formatter: formatarDataEntrega,
    sort: true,
  },
  {
    dataField: 'valor.viaData.entrega.entregas[0].dataPrevisao',
    text: 'Previsão de Entrega',
    formatter: formatarDataPrevisao,
    sort: true,
  },
  {
    dataField: 'previsaoEntrega',
    text: 'Previsão de Entrega',
    formatter: (cell) => {
      if (!cell) return '';
      return new Date(cell).toLocaleDateString('pt-BR');
    },
    sort: true,
  },
  {
    dataField: 'obs',
    text: 'Observação',
    sort: true,
  },
];

const defaultSorted = [
  {
    dataField: 'resgatadoEm',
    order: 'desc',
  },
];

export default function Resgates() {
  const dispatch = useDispatch();
  const parceiro = useSelector((state) => state.parceiro);
  const [data, setData] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    sizePerPage: 10,
    totalSize: 0
  });

  useEffect(() => {
    buscarResgates();
    dispatch(atualizarSaldo(parceiro.cpf));
  }, []);

  const buscarResgates = async () => {
    setBuscando(true);
    try {
      const params = {
        pagina: pagination.page,
        limite: pagination.sizePerPage,
        busca: ''
      };

      const resultado = await api.get('/api/resgates', { params });
      
      if (resultado?.data?.resgates) {
        setData(resultado.data.resgates.rows || []);
        setPagination(prev => ({
          ...prev,
          totalSize: resultado.data.resgates.count || 0
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar resgates:', error);
      setData([]);
    }
    setBuscando(false);
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setPagination(prev => ({
      ...prev,
      page,
      sizePerPage
    }));
    
    setTimeout(() => {
      buscarResgates();
    }, 0);
  };

  const paginationOptions = {
    page: pagination.page,
    sizePerPage: pagination.sizePerPage,
    totalSize: pagination.totalSize,
    showTotal: true,
    sizePerPageList: [
      { text: '10', value: 10 },
      { text: '25', value: 25 },
      { text: '50', value: 50 },
      { text: 'Todos', value: pagination.totalSize }
    ]
  };

  return (
    <Container className="mt-5 mb-5">
      <div>
        <h5>RESGATES</h5>
        <hr className="hr-loja mt-0" />
      </div>
      <div>
        <BootstrapTable
          remote={{ 
            pagination: true,
            sort: false,
            filter: false
          }}
          loading={buscando}
          onTableChange={handleTableChange}
          noDataIndication={() =>
            buscando ? (
              <span>
                Carregando
                <i className="fas fa-circle-notch fa-spin ml-3"></i>
              </span>
            ) : (
              <span>Sem resultados</span>
            )
          }
          bootstrap4
          keyField="codigo"
          data={data}
          columns={columns}
          pagination={paginationFactory(paginationOptions)}
          defaultSorted={defaultSorted}
          headerClasses="tabela-header-claro"
          bodyClasses="tabela-body-claro"
        />
      </div>
    </Container>
  );
}
